import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import './App.css';
import TopNavbar from './components/TopNavbar/TopNavbar';
import SectionHeader from './components/SectionHeader/SectionHeader';
import StatCard from './components/StatCard/StatCard';
import WorkImpactCard from './components/WorkImpactCard/WorkImpactCard';
import GradientText from './components/GradientText/GradientText';
import CommunityEventCard from './components/CommunityEventCard/CommunityEventCard';
import BackdropPanel from './components/BackdropPanel/BackdropPanel';
import PeopleCard from './components/PeopleCard/PeopleCard';
import ProjectCard from './components/ProjectCard/ProjectCard';
import HorizontalScroll from './components/HorizontalScroll/HorizontalScroll';
import InitiativeSummary from './components/InitiativeSummary/InitiativeSummary';
import InitiativeCard from './components/InitiativeCard/InitiativeCard';
import InnovationCard from './components/InnovationCard/InnovationCard';
import PhotoCard from './components/PhotoCard/PhotoCard';
import CustomerVisitCard from './components/CustomerVisitCard/CustomerVisitCard';
import FutureFocusCard from "./components/FutureFocusCard/FutureFocusCard"
import CountUpValue from './components/CountUpValue/CountUpValue';
import ImagePreview from './components/ImagePreview/ImagePreview';
import ProtectedRoute from './auth/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import { getImageSrc } from './utils/imageUtils';
import {
  ACTIVE_INITIATIVES,
  COMMUNITY_EVENTS,
  CUSTOMER_LEADERSHIP_VISITS,
  HIGHLIGHT_CARDS,
  INNOVATION,
  LIFE_BEYOND_WORK,
  MILESTONES_RECOGNITION,
  NAV_ITEMS,
  PEOPLE_BEHIND_IMPACT,
  PROJECTS_INFO,
  WORK_IMPACT_METRICS,
  WORKSHOPS_CONDUCTED,
  FUTURE_FOCUS
} from './constants';

const getUniqueSpeakerCount = (events = []) => {
  const speakerSet = new Set();
  events.forEach((event) => {
    (event.speakers || []).forEach((speaker) => speakerSet.add(speaker));
  });
  return speakerSet.size;
};

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<MainShell />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function MainShell() {
  useEffect(() => {
    const raw = window.location.hash || '';
    const id = raw.startsWith('#') ? raw.slice(1) : raw;
    if (!id) return;
    if (!NAV_ITEMS.some((it) => it.id === id)) return;

    const el = document.getElementById(id);
    // Use rAF to ensure layout is ready on first paint.
    let rafId = 0;
    if (el) {
      rafId = requestAnimationFrame(() => el.scrollIntoView({ block: 'start' }));
    }
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  const [previewImage, setPreviewImage] = useState(null);
  const handlePreview = (src, alt) => setPreviewImage({ src, alt });
  const closePreview = () => setPreviewImage(null);

  const initiatives = ACTIVE_INITIATIVES.initiatives;
  const communityEvents = COMMUNITY_EVENTS.events;
  const communitySpeakerCount = getUniqueSpeakerCount(communityEvents);
  const communityCitiesReached = new Set(
    communityEvents
      .map((event) => event.city || event.location)
      .filter(Boolean),
  ).size;
  const communityCommunitiesCount = communityEvents.length;
  const milestones = MILESTONES_RECOGNITION.milestones;
  const milestoneTotal = milestones.length;
  const milestoneIcon = getImageSrc('cup.svg');
  const spotAwards = MILESTONES_RECOGNITION.spot_awards;
  const spotAwardsTotal = spotAwards.length;
  const spotAwardsIcon = getImageSrc('medal_2.svg');
  const promotions = MILESTONES_RECOGNITION.promotions;
  const promotionsTotal = promotions.length;
  const promotionsIcon = getImageSrc('medal_1.svg');
  const certifications = MILESTONES_RECOGNITION.certifications;
  const certificationCount = certifications.length;
  const certificationStyleCount = Math.min(9, Math.max(1, certificationCount));
  const certificationsTotal = certifications.reduce(
    (total, certification) => total + certification.count,
    0,
  );
  const certificationRows = Math.min(
    3,
    Math.max(1, Math.ceil(certificationStyleCount / 3)),
  );
  const buildPeopleGridClasses = (count, scrollLimit) => ([
    'App-peopleGrid',
    count > scrollLimit ? 'App-peopleGrid--scroll' : '',
  ]
    .filter(Boolean)
    .join(' '));
  const certificationsGridClasses = buildPeopleGridClasses(certificationCount, 9);
  const communityMetrics = [
    // { value: COMMUNITY_EVENTS.speaking_events, label: 'Speaking Events' },
    { value: communityCitiesReached, label: 'Cities Reached' },
    { value: communityCommunitiesCount, label: 'Communities' },
    { value: communitySpeakerCount, label: 'Speakers' },
  ];
  const innovationMetrics = [
    { value: INNOVATION.pocs, label: 'POCs' },
    { value: INNOVATION.unique_technologies, label: 'Unique Technologies' },
  ];
  const lifeBeyondEvents = LIFE_BEYOND_WORK.events;
  const customerVisits = CUSTOMER_LEADERSHIP_VISITS.visits;
  const peopleBehindImpact = PEOPLE_BEHIND_IMPACT.people;
  const focus_events= FUTURE_FOCUS.focus_events;
  const peopleCount = peopleBehindImpact.length;
  const peopleRows = Math.min(2, Math.max(1, Math.ceil(peopleCount / 4)));
  const peopleGridClasses = buildPeopleGridClasses(peopleCount, 8);
  const peopleStyleCount = Math.max(1, peopleCount);

  return (
    <div className="App">
      <TopNavbar
        items={NAV_ITEMS}
        height={100}
      />

      <main className="App-main">
        <Section
          id="highlights"
          header={
            <SectionHeader
              gradientText="2025"
              title="The Year at a Glance"
            />
          }
          bodyClassName="App-sectionBody--full"
        >
          <div className="App-highlightGrid">
            {HIGHLIGHT_CARDS.map((card) => (
              <StatCard key={card.title} {...card} />
            ))}
          </div>
        </Section>
        <Section id="work-impact"
          header={
            <SectionHeader
              title="What We Built & Delivered"
              button="View All Works"
              onButtonClick={() => {
                console.log('View All');
              }}
            />
          }
          bodyClassName="App-sectionBody--full"
          decorations={(
            <div className="App-workImpactDecor">
              <span className="App-workImpactDecor__box" />
              <span className="App-workImpactDecor__box" />
              <span className="App-workImpactDecor__box" />
            </div>
          )}
        >
          <div className="App-workImpact">
            <WorkImpactCard metrics={WORK_IMPACT_METRICS} />
            <div className="App-workImpactProjects">
              <ProjectsCarousel projects={PROJECTS_INFO} />
            </div>
            <WorkImpactCard
              title={WORKSHOPS_CONDUCTED.title}
              workshops={WORKSHOPS_CONDUCTED.workshops}
              showWorkshops
            />
          </div>
          <div className="App-activeInitiatives">
            <div className="App-activeInitiativesRow">
              <InitiativeSummary
                count={initiatives.length}
                topTitle={ACTIVE_INITIATIVES.title}
                buttonLabel="View All Initiatives"
                onButtonClick={() => {
                  console.log('View All Initiatives');
                }}
              />
              <div className="App-activeInitiativesCards">
                {initiatives.slice(0, 3).map((initiative, index) => (
                  <InitiativeCard
                    key={`${initiative.title}-${index}`}
                    title={initiative.title}
                    description={initiative.description}
                    tags={initiative.tags}
                    tagPosition="top"
                  />
                ))}
              </div>
            </div>
            <HorizontalScroll
              fullBleed
              className="App-activeInitiativesScroll"
              backgroundColor="rgba(106, 114, 130, 0.2)"
              padding="2em"
            >
              {initiatives.slice(3).map((initiative, index) => (
                <div
                  key={`${initiative.title}-scroll-${index}`}
                  className="App-activeInitiativesScrollItem"
                >
                  <InitiativeCard
                    title={initiative.title}
                    description={initiative.description}
                    tags={initiative.tags}
                    tagPosition="bottom"
                  />
                </div>
              ))}
            </HorizontalScroll>
          </div>
          <div className="App-communityEvents">
            <div className="App-communityEventsDecor" aria-hidden="true">
              <span className="App-communityEventsDecor__box" />
              <span className="App-communityEventsDecor__box" />
              <span className="App-communityEventsDecor__box" />
            </div>
            <GradientText
              className="App-communityEventsTitle"
              text={COMMUNITY_EVENTS.title}
              showGlow
            />
            <div className="App-communityEventsStats">
              <WorkImpactCard
                className="WorkImpactCard--community"
                metrics={communityMetrics}
              />
            </div>
            <HorizontalScroll
              fullBleed
              className="App-communityEventsScroll"
              backgroundColor="rgba(106, 114, 130, 0.2)"
              padding="2em"
            >
              {COMMUNITY_EVENTS.events.map((event, index) => (
                <div
                  key={`${event.name}-${event.date}-${index}`}
                  className="App-communityEventsScrollItem"
                >
                  <CommunityEventCard
                    name={event.name}
                    topic={event.topic}
                    date={event.date}
                    city={event.city}
                    speakers={event.speakers}
                  />
                </div>
              ))}
            </HorizontalScroll>
          </div>
        </Section>
        <Section
          id="growth-recognition"
          header={(
            <SectionHeader
              gradientText="2025"
              title={MILESTONES_RECOGNITION.title}
            />
          )}
          bodyClassName="App-sectionBody--full"
        >
          <div className="App-milestonesWrapper">
            <div className="App-milestones">
              <BackdropPanel
                className="App-milestonesPanel"
                title="AWARDS"
                total={milestoneTotal}
                icon={(
                  <img src={milestoneIcon} alt="Awards trophy" />
                )}
              >
                <div className="App-milestonesGrid">
                  {milestones.slice(0, 4).map((milestone, index) => (
                    <PeopleCard
                      key={`${milestone.title}-${milestone.name}-${index}`}
                      {...milestone}
                    />
                  ))}
                </div>
              </BackdropPanel>
              <HorizontalScroll
                fullBleed
                className="App-milestonesScroll"
                backgroundColor="rgba(106, 114, 130, 0.2)"
                padding="2em"
              >
                {milestones.slice(4).map((milestone, index) => (
                  <div
                    key={`${milestone.title}-${milestone.name}-scroll-${index}`}
                    className="App-milestonesScrollItem"
                  >
                    <PeopleCard {...milestone} />
                  </div>
                ))}
              </HorizontalScroll>
            </div>
            <div className="App-recognitionGroup">
              <BackdropPanel
                className="App-recognitionPanel"
                title="SPOT AWARDS"
                total={spotAwardsTotal}
                icon={(
                  <img src={spotAwardsIcon} alt="Spot awards medal" />
                )}
              >
                <div className="App-recognitionGrid">
                  {spotAwards.slice(0, 4).map((award, index) => (
                    <PeopleCard
                      key={`${award.name}-${award.date}-${index}`}
                      {...award}
                    />
                  ))}
                </div>
              </BackdropPanel>
              <HorizontalScroll
                fullBleed
                className="App-recognitionScroll"
                backgroundColor="rgba(106, 114, 130, 0.2)"
                padding="2em"
              >
                {spotAwards.slice(4).map((award, index) => (
                  <div
                    key={`${award.name}-${award.date}-scroll-${index}`}
                    className="App-recognitionScrollItem"
                  >
                    <PeopleCard {...award} />
                  </div>
                ))}
              </HorizontalScroll>
            </div>
            <div className="App-recognitionGroup">
              <BackdropPanel
                className="App-recognitionPanel"
                title="PROMOTIONS"
                total={promotionsTotal}
                icon={(
                  <img src={promotionsIcon} alt="Promotions medal" />
                )}
              >
                <div className="App-recognitionGrid">
                  {promotions.slice(0, 4).map((promotion, index) => (
                    <PeopleCard
                      key={`${promotion.name}-${index}`}
                      {...promotion}
                    />
                  ))}
                </div>
              </BackdropPanel>
              <HorizontalScroll
                fullBleed
                className="App-recognitionScroll"
                backgroundColor="rgba(106, 114, 130, 0.2)"
                padding="2em"
              >
                {promotions.slice(4).map((promotion, index) => (
                  <div
                    key={`${promotion.name}-scroll-${index}`}
                    className="App-recognitionScrollItem"
                  >
                    <PeopleCard {...promotion} />
                  </div>
                ))}
              </HorizontalScroll>
            </div>
            <div className="App-certifications">
              <div className="App-certificationsRow">
                <InitiativeSummary
                  count={certificationsTotal}
                  bottomTitle="Total Certifications"
                />
                <div className="App-certificationsScroll">
                  <div
                    className={`${certificationsGridClasses} App-certificationsGrid`}
                    style={{
                      '--people-count': `${certificationStyleCount}`,
                      '--people-rows': `${certificationRows}`,
                      '--people-max-columns': 3,
                    }}
                  >
                    {certifications.map((certification, index) => (
                      <div
                        key={`${certification.vendor}-${index}`}
                        className="App-certificationsCard"
                      >
                        <CountUpValue
                          className="App-certificationsCount"
                          value={certification.count}
                        />
                        <div className="App-certificationsVendor">{certification.vendor}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>
        <Section
          id="innovation"
          header={(
            <SectionHeader
              gradientText="2025"
              title={INNOVATION.title}
            />
          )}
          bodyClassName="App-sectionBody--full"
        >
          <div className="App-innovation">
            <WorkImpactCard
              title="POCs Completed"
              metrics={innovationMetrics}
            />
            <HorizontalScroll
              fullBleed
              className="App-innovationScroll"
              backgroundColor="linear-gradient(10deg, rgba(0, 211, 242, 0.8), rgba(225, 42, 251, 0.7))"
              padding="2em"
            >
              {INNOVATION.innovations.map((innovation, index) => (
                <div
                  key={`${innovation.title}-${index}`}
                  className="App-innovationScrollItem"
                >
                  <InnovationCard {...innovation} />
                </div>
              ))}
            </HorizontalScroll>
          </div>
        </Section>
        <Section
          id="culture-community"
          header={(
            <SectionHeader
              gradientText="2025"
              title={LIFE_BEYOND_WORK.title}
            />
          )}
          bodyClassName="App-sectionBody--full"
        >
          <div className="App-cultureCommunity">
            <HorizontalScroll
              fullBleed
              className="App-lifeBeyondScroll"
              backgroundColor="rgba(106, 114, 130, 0.2)"
              padding="2em"
              measureItems={false}
            >
              {lifeBeyondEvents.map((event, index) => (
                <div
                  key={`${event.title}-${index}`}
                  className="App-lifeBeyondScrollItem"
                >
                  <PhotoCard
                    title={event.title}
                    image_url={event.image_url}
                    onPreview={handlePreview}
                  />
                </div>
              ))}
            </HorizontalScroll>
            <div className="App-customerVisits">
              <span className="App-actionLabel">{CUSTOMER_LEADERSHIP_VISITS.title}</span>
              <HorizontalScroll
                fullBleed
                className="App-customerVisitsScroll"
                backgroundColor="rgba(106, 114, 130, 0.2)"
                padding="2em"
              >
                {customerVisits.map((visit, index) => (
                  <div
                    key={`${visit.title}-${visit.location}-${index}`}
                    className="App-customerVisitsScrollItem"
                  >
                    <CustomerVisitCard {...visit} onPreview={handlePreview} />
                  </div>
                ))}
              </HorizontalScroll>
            </div>
          </div>
        </Section>
        <Section
          id="people"
          header={<SectionHeader title={PEOPLE_BEHIND_IMPACT.title} />}
          bodyClassName="App-sectionBody--full"
        >
          <div className="App-people">
            <BackdropPanel className="App-peoplePanel" title="LEADERSHIP">
              <div
                className={peopleGridClasses}
                data-count={peopleCount}
                style={{
                  '--people-count': `${peopleStyleCount}`,
                  '--people-rows': `${peopleRows}`,
                }}
              >
                {peopleBehindImpact.map((person, index) => (
                  <PeopleCard
                    key={`${person.name}-${person.designation}-${index}`}
                    {...person}
                  />
                ))}
              </div>
            </BackdropPanel>
          </div>
        </Section>
        <Section
          id="future-focus"
          header={(
            <SectionHeader
              gradientText="2026"
              title={FUTURE_FOCUS.title}
            />
          )}
          bodyClassName="App-sectionBody--full"
        >
          <div className="App-FutureFocusBody">
            {focus_events.map((event,index)=>(
              <FutureFocusCard key={event.title} title={event.title} keypoints={event.keypoints} icon={ event.icon} />
            ))}
          </div>
        </Section>

      </main>
      <footer className="App-footer">
        <div className="App-footerInner">
          <span className="App-footerText">© 2025 Presidio Digital Practice India</span>
          <span className="App-footerCenter">Do something great every day! 🎉</span>
          <span className="App-footerRight">
            Made with
            <img
              className="App-footerHeart"
              src={getImageSrc('heart.svg')}
              alt="heart icon"
            />
          </span>
        </div>
      </footer>
      <ImagePreview
        imageUrl={previewImage?.src}
        alt={previewImage?.alt}
        onClose={closePreview}
      />
    </div>
  );
}

function Section({ id, title, header, bodyClassName, children, decorations }) {
  const bodyClasses = ['App-sectionBody', bodyClassName].filter(Boolean).join(' ');
  return (
    <section id={id} className="App-section">
      <div className="App-sectionInner">
        {decorations ? (
          <div className="App-sectionDecorations" aria-hidden="true">
            {decorations}
          </div>
        ) : null}
        <div className="App-sectionContent">
          {header ?? <h2 className="App-sectionTitle">{title}</h2>}
          <div className={bodyClasses}>{children}</div>
        </div>
      </div>
    </section>
  );
}

function ProjectsCarousel({ projects = [] }) {
  const trackRef = useRef(null);
  const itemRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const activeIndexRef = useRef(0);
  const rafIdRef = useRef(0);

  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, projects.length);
  }, [projects.length]);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  const updateActiveIndex = useCallback(() => {
    const track = trackRef.current;
    const items = itemRefs.current.filter(Boolean);
    if (!track || items.length === 0) return;

    const maxScrollLeft = track.scrollWidth - track.clientWidth;
    if (track.scrollLeft <= 1) {
      setActiveIndex(0);
      return;
    }
    if (maxScrollLeft > 0 && track.scrollLeft >= maxScrollLeft - 1) {
      setActiveIndex(items.length - 1);
      return;
    }

    const trackRect = track.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;

    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    items.forEach((item, index) => {
      const rect = item.getBoundingClientRect();
      const itemCenter = rect.left + rect.width / 2;
      const distance = Math.abs(itemCenter - trackCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    const currentIndex = activeIndexRef.current;
    const currentItem = items[currentIndex];
    if (currentItem) {
      const rect = currentItem.getBoundingClientRect();
      const currentCenter = rect.left + rect.width / 2;
      const currentDistance = Math.abs(currentCenter - trackCenter);
      const hysteresis = Math.min(36, trackRect.width * 0.08);

      if (currentDistance <= closestDistance + hysteresis) {
        return;
      }
    }

    setActiveIndex((prev) => (prev === closestIndex ? prev : closestIndex));
  }, []);

  const handleScroll = useCallback(() => {
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = requestAnimationFrame(updateActiveIndex);
  }, [updateActiveIndex]);

  useEffect(() => {
    handleScroll();
    window.addEventListener('resize', handleScroll);

    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      window.removeEventListener('resize', handleScroll);
    };
  }, [handleScroll, projects.length]);

  return (
    <div className="ProjectCarousel">
      <HorizontalScroll trackRef={trackRef} onScroll={handleScroll}>
        {projects.map((project, index) => (
          <div
            key={`${project.title}-${index}`}
            className="ProjectCarousel__item"
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
          >
            <ProjectCard {...project} />
          </div>
        ))}
      </HorizontalScroll>
      <div className="ProjectCarousel__indicators" aria-hidden="true">
        {projects.map((project, index) => (
          <span
            key={`${project.title}-indicator-${index}`}
            className={`ProjectCarousel__indicator ${index === activeIndex ? 'is-active' : ''}`}
          />
        ))}
      </div>
    </div>
  );
}

export default App;
